import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import colors from '../styles/colors';

const MenuContainer = styled.section`
  padding: 1.5rem 0;
  background: ${colors.background.white};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  h2 {
    font-size: 2.5rem;
    font-weight: 700;
    color: ${colors.text.primary};
    margin-bottom: 1rem;
    position: relative;
    display: inline-block;

    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 4px;
      background: ${colors.primary.green};
      border-radius: 2px;
    }
  }

  p {
    font-size: 1.1rem;
    color: ${colors.text.secondary};
    max-width: 600px;
    margin: 0 auto;
  }
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const MenuCategory = styled(motion.div)`
  background: ${colors.background.light};
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 5px 15px rgba(0,0,0,0.08);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const CategoryTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${colors.primary.green};
  margin-bottom: 1.5rem;
  text-align: center;
`;

const MenuItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MenuItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    transform: translateX(5px);
  }
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const ItemName = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 0.5rem 0;
`;

const ItemDescription = styled.p`
  font-size: 0.9rem;
  color: ${colors.text.secondary};
  margin: 0;
  line-height: 1.4;
`;

const ItemPrice = styled.span`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${colors.primary.green};
`;

const CTAButton = styled(motion.button)`
  background: ${colors.primary.green};
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: block;
  margin: 2rem auto 0 auto;
  box-shadow: 0 4px 15px rgba(0, 150, 57, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 150, 57, 0.4);
    background: ${colors.primary.blue};
  }
`;

const Menu = () => {
  const { t } = useTranslation();

  const handleMenuRequest = () => {
    // Navegar na mesma aba para o menu completo unificado
    window.location.href = '/full-menu';
  };

  return (
    <MenuContainer id="menu">
      <Container>
        <Header>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            {t('menu.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {t('menu.subtitle')}
          </motion.p>
        </Header>

        <MenuGrid>
          <MenuCategory
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0 }}
            viewport={{ once: true }}
          >
            <CategoryTitle>
              {t('menu.lunchMenu')}
            </CategoryTitle>
            <MenuItems>
              <MenuItem>
                <ItemInfo>
                  <ItemName>Mineirinho</ItemName>
                  <ItemDescription>Arroz, Feijão, Bife di Manzo, Batata frita ou mandioca</ItemDescription>
                </ItemInfo>
                <ItemPrice>€14.00</ItemPrice>
              </MenuItem>
              <MenuItem>
                <ItemInfo>
                  <ItemName>Bahiana</ItemName>
                  <ItemDescription>Arroz / Feijão / Manzo / Ovo / Batata frita</ItemDescription>
                </ItemInfo>
                <ItemPrice>€14.00</ItemPrice>
              </MenuItem>
              <MenuItem>
                <ItemInfo>
                  <ItemName>Gaúcho</ItemName>
                  <ItemDescription>Arroz, Feijão, Picanha, Vinagrete, Farofa</ItemDescription>
                </ItemInfo>
                <ItemPrice>€18.00</ItemPrice>
              </MenuItem>
            </MenuItems>
          </MenuCategory>

          <MenuCategory
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <CategoryTitle>
              {t('menu.appetizerMenu')}
            </CategoryTitle>
            <MenuItems>
              <MenuItem>
                <ItemInfo>
                  <ItemName>Churrasquinho</ItemName>
                  <ItemDescription>Churrasquinho</ItemDescription>
                </ItemInfo>
                <ItemPrice>€3.50</ItemPrice>
              </MenuItem>
              <MenuItem>
                <ItemInfo>
                  <ItemName>Mix de Pastéis</ItemName>
                  <ItemDescription>9 unidades - Carne e Queijo</ItemDescription>
                </ItemInfo>
                <ItemPrice>€10.00</ItemPrice>
              </MenuItem>
              <MenuItem>
                <ItemInfo>
                  <ItemName>Caipirinha</ItemName>
                  <ItemDescription>Cocktail nacional brasileiro</ItemDescription>
                </ItemInfo>
                <ItemPrice>€5.00</ItemPrice>
              </MenuItem>
            </MenuItems>
          </MenuCategory>
        </MenuGrid>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <CTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleMenuRequest}
          >
            {t('menu.viewFullMenu', { defaultValue: 'Ver Menu Completo' })}
          </CTAButton>
        </motion.div>
      </Container>
    </MenuContainer>
  );
};

export default Menu;
